/* Performance Analytics — mock data layer.
 *
 * Ported verbatim from the alpaca_performance design mock-up so the dashboards
 * render the SAME illustrative numbers the design showed.  This is demo data
 * only — no API wiring (per the request: "no need to link with real data now").
 * When the real Data-Warehouse feed lands (CLAUDE.md §17.3), these builders are
 * replaced by `IReportProvider` queries; the page configs stay unchanged. */

export const CLIENTS = ['AQT', 'BAT', 'COA', 'DWO', 'HHD', 'JEL', 'RRA', 'WCO'] as const;
export type ClientCode = (typeof CLIENTS)[number];

export const CLIENT_NAMES: Record<string, string> = {
  AQT: 'Aqua Tech Labs', BAT: 'Bay Area Treatment', COA: 'City of Austin',
  DWO: 'City of Houston (DWO)', HHD: 'Hill Hollow District', JEL: 'Jenkins Environmental',
  RRA: 'Rio Reclamation Authority', WCO: 'Westcoast Water Co.',
};

export const TESTS = [
  'EPA 200.8', 'EPA 300.0', 'EPA 524.2', 'Total Coliform', 'Colilert-18',
  'Alkalinity', 'LSI Panel', 'Disinfection Byproducts', 'Nitrate/Nitrite', 'Metals Panel',
];
export const ANALYSTS = ['M. Reyes', 'J. Okafor', 'S. Patel', 'T. Nguyen', 'A. Brooks', 'L. Schmidt'];
export const INSTRUMENTS = ['ICP-MS 1', 'ICP-MS 2', 'GC-MS 1', 'GC-MS 2', 'IC-1', 'Spec-1'];
export const REPS = ['Chris D.', 'Wilson T.', 'Selvi R.'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const DAYS28 = Array.from({ length: 28 }, (_, i) => `D${i + 1}`);
export const WEEKS12 = Array.from({ length: 12 }, (_, i) => `Wk ${i + 1}`);

/** Linear-congruential PRNG — same constants as the design, so the seeded
 *  numbers reproduce the mock-up exactly. */
export function seedRand(seed: number): () => number {
  let s = seed;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

/** A gently-drifting series of `n` integers, seeded — the design's workhorse. */
export function series(n: number, base: number, vol: number, seed: number): number[] {
  const r = seedRand(seed);
  const out: number[] = [];
  let v = base;
  for (let i = 0; i < n; i++) { v = Math.max(0, v + (r() - 0.45) * vol); out.push(Math.round(v)); }
  return out;
}

/** Seeded single sample (design's `series(1,…)[0]` shorthand). */
export const one = (base: number, vol: number, seed: number): number => series(1, base, vol, seed)[0] ?? 0;

/** Seeded pick from an array. */
export function pick<T>(arr: readonly T[], seed: number): T {
  const r = seedRand(seed);
  return arr[Math.floor(r() * arr.length)] as T;
}

/** Deterministic 0..1 — replaces the design's `Math.random()` in the few cost/
 *  price spots so the values are stable across re-renders (no flicker). */
export const det = (seed: number): number => seedRand(seed * 131 + 7)();

export const PALETTE = {
  orange: '#e8670a', orangeLight: '#f6b27a', navy: '#1a1a2e', blue: '#185fa5',
  blueLight: '#a9cdec', green: '#1a9e5c', greenLight: '#a9e0c2', red: '#d33a3a',
  redLight: '#f0b3b3', amber: '#d99a1b', amberLight: '#f3d99a', grey: '#c7c7c7',
} as const;

/* ── v8 dimensions — used by Revenue Explorer + Concentration Risk ── */
export const DEPARTMENTS = ['Inorganics', 'Organics', 'Microbiology', 'Wet Chemistry'];
export const MATRICES = ['DW', 'WW', 'Soil', 'Air'];
export const MATRIX_NAMES: Record<string, string> = { DW: 'Drinking Water', WW: 'Wastewater', Soil: 'Soil', Air: 'Air' };
export const PROGRAMS = ['PFAS', 'UCMR5', 'Lead & Copper', 'TCEQ'];
export const SAMPLE_TYPES = ['Routine', 'Rush', 'Compliance', 'Special Project'];
export const PROJECTS = ['DWO Phase II', 'EWPP Monitoring', 'AQT Industrial Permit', 'HHD Routine Program', 'BAT Annual Compliance'];
export const CLIENT_REVENUE_WEIGHT: Record<string, number> = { AQT: 0.19, BAT: 0.07, COA: 0.10, DWO: 0.24, HHD: 0.09, JEL: 0.05, RRA: 0.11, WCO: 0.15 };
